import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, FileText, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = [
  { value: "miet", label: "Mietverwaltung" },
  { value: "weg", label: "WEG-Verwaltung" },
  { value: "sondereigentum", label: "Sondereigentumsverwaltung" },
  { value: "gewerbe", label: "Gewerbeverwaltung" },
  { value: "sonstige", label: "Sonstige" },
];
const FEE_INTERVALS = [
  { value: "monthly", label: "Monatlich" },
  { value: "quarterly", label: "Quartalsweise" },
  { value: "yearly", label: "Jährlich" },
];

const fmtEUR = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export default function ManagementContracts() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/management-contracts");
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/contacts", { params: { limit: 1000 } }).then((r) => setContacts(r.data.filter((c) => (c.roles || []).includes("owner"))));
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/documents").then((r) => setDocuments(r.data));
  }, []);

  const contactMap = useMemo(() => Object.fromEntries(contacts.map((c) => [c.id, c])), [contacts]);
  const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);

  const remove = async (id) => {
    if (!await window._domivusConfirm("Verwaltervertrag löschen?")) return;
    await api.delete(`/management-contracts/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const ownerName = (c) => c ? (c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()) : "—";

  return (
    <div className="space-y-4" data-testid="management-contracts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Verwalterverträge</h1>
          <p className="text-sm text-slate-500 mt-1">Mietverwaltung, WEG, Sondereigentum, Gewerbe – Vertragsumfang mit Objekt-Verknüpfung.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="add-contract-btn">
              <Plus size={16} /> Neuer Vertrag
            </Button>
          </DialogTrigger>
          <ContractDialog
            key={open ? (editing?.id || "new") : "closed"}
            editing={editing}
            contacts={contacts}
            properties={properties}
            documents={documents}
            onSaved={() => { setOpen(false); setEditing(null); load(); }}
          />
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <Th>Vertrag</Th>
              <Th>Typ</Th>
              <Th>Eigentümer</Th>
              <Th>Objekte</Th>
              <Th className="text-right">Honorar</Th>
              <Th className="w-24"></Th>
            </tr>
          </thead>
          <tbody data-testid="contracts-tbody">
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500">
                <Briefcase size={32} className="mx-auto mb-2 text-slate-300" />
                Noch keine Verwalterverträge erfasst.
              </td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`contract-row-${c.id}`}>
                <Td>
                  <div className="font-medium text-slate-900">{c.title}</div>
                  {c.contract_number && <div className="text-[11px] text-slate-500 font-mono">{c.contract_number}</div>}
                  {(c.linked_document_ids || []).length > 0 && (
                    <div className="text-[11px] text-emerald-700 mt-0.5 flex items-center gap-1">
                      <FileText size={11} /> {c.linked_document_ids.length} Dokument(e)
                    </div>
                  )}
                </Td>
                <Td><span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{TYPES.find((t) => t.value === c.type)?.label}</span></Td>
                <Td className="text-xs text-slate-600">{ownerName(contactMap[c.owner_contact_id])}</Td>
                <Td className="text-xs text-slate-600">
                  {(c.property_ids || []).slice(0, 2).map((pid) => propMap[pid]?.name).filter(Boolean).join(", ")}
                  {(c.property_ids || []).length > 2 && <span className="text-slate-400"> +{c.property_ids.length - 2}</span>}
                </Td>
                <Td className="text-right tabular-nums">
                  {c.fee_amount ? `${fmtEUR(c.fee_amount)} / ${FEE_INTERVALS.find((i) => i.value === c.fee_interval)?.label.toLowerCase()}` : "—"}
                </Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`contract-edit-${c.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700" onClick={() => remove(c.id)}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
}

function ContractDialog({ editing, contacts, properties, documents, onSaved }) {
  const blank = {
    title: "", contract_number: "", type: "miet", owner_contact_id: contacts[0]?.id || "",
    property_ids: [], property_assignments: [],
    start_date: "", end_date: "", notice_period_months: 3, auto_renewal_months: 12,
    fee_amount: 0, fee_interval: "monthly", fee_per_unit: false,
    is_net: false, vat_rate: 19.0, fee_history: [],
    scope_description: "",
    linked_document_ids: [], notes: "", is_active: true,
  };
  const [form, setForm] = useState(() => editing ? { ...blank, ...editing } : blank);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  useEffect(() => { setForm(editing ? { ...blank, ...editing } : blank); /* eslint-disable-next-line */ }, [editing]);

  // 2026-08 — Objekt-Zuordnung mit Gültig-Ab/Bis (nicht mehr nur einfaches Toggle)
  const toggleProperty = (id) => {
    const list = form.property_assignments || [];
    const idx = list.findIndex((a) => a.property_id === id);
    if (idx >= 0) {
      set("property_assignments", list.filter((_, i) => i !== idx));
    } else {
      set("property_assignments", [...list, { property_id: id, valid_from: form.start_date || "", valid_until: "", note: "" }]);
    }
  };
  const updateAssignment = (id, patch) => {
    set("property_assignments", (form.property_assignments || []).map((a) => a.property_id === id ? { ...a, ...patch } : a));
  };
  const isAssigned = (id) => (form.property_assignments || []).some((a) => a.property_id === id);
  const getAssignment = (id) => (form.property_assignments || []).find((a) => a.property_id === id);

  // 2026-08 — Honorar-Historie (rückwirkend änderbar)
  const addFeeEntry = () => {
    const today = new Date().toISOString().slice(0, 10);
    const list = form.fee_history || [];
    // Übernimm aktuelle Werte als Voreinstellung
    const latest = list.length ? list[list.length - 1] : null;
    set("fee_history", [...list, {
      valid_from: today,
      valid_until: "",
      fee_amount: Number(form.fee_amount) || (latest?.fee_amount ?? 0),
      fee_interval: form.fee_interval || latest?.fee_interval || "monthly",
      fee_per_unit: form.fee_per_unit ?? latest?.fee_per_unit ?? false,
      is_net: form.is_net ?? latest?.is_net ?? false,
      vat_rate: form.vat_rate ?? latest?.vat_rate ?? 19,
      note: "",
    }]);
  };
  const updateFeeEntry = (idx, patch) => {
    set("fee_history", (form.fee_history || []).map((h, i) => i === idx ? { ...h, ...patch } : h));
  };
  const removeFeeEntry = (idx) => {
    set("fee_history", (form.fee_history || []).filter((_, i) => i !== idx));
  };

  const toggleDocId = (id) => {
    const cur = new Set(form.linked_document_ids || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    set("linked_document_ids", Array.from(cur));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Titel fehlt"); return; }
    if (!form.owner_contact_id) { toast.error("Eigentümer wählen"); return; }
    // Assignments säubern (leere valid_until → null)
    const assignments = (form.property_assignments || []).map((a) => ({
      property_id: a.property_id,
      valid_from: a.valid_from || null,
      valid_until: a.valid_until || null,
      note: a.note || null,
    }));
    const feeHistory = (form.fee_history || []).map((h) => ({
      ...h,
      fee_amount: Number(h.fee_amount) || 0,
      vat_rate: Number(h.vat_rate) || 19,
      valid_until: h.valid_until || null,
    }));
    const payload = {
      ...form,
      property_assignments: assignments,
      property_ids: assignments.map((a) => a.property_id),   // Legacy-Feld ableiten
      fee_history: feeHistory,
      fee_amount: Number(form.fee_amount) || null,
      notice_period_months: Number(form.notice_period_months) || null,
      auto_renewal_months: Number(form.auto_renewal_months) || null,
      is_net: !!form.is_net,
      vat_rate: Number(form.vat_rate) || 19,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    };
    try {
      if (editing) { await api.patch(`/management-contracts/${editing.id}`, payload); toast.success("Aktualisiert"); }
      else { await api.post("/management-contracts", payload); toast.success("Angelegt"); }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contract-dialog">
      <DialogHeader>
        <DialogTitle>{editing ? "Verwaltervertrag bearbeiten" : "Neuer Verwaltervertrag"}</DialogTitle>
        <DialogDescription>Vertragsdaten · Objekte mit Gültigkeit · Honorar-Historie · Dokumente</DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <F label="Titel"><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="z. B. Mietverwaltungsvertrag Eigentümer Müller" data-testid="contract-title" /></F>
          <F label="Vertragsnummer"><Input value={form.contract_number} onChange={(e) => set("contract_number", e.target.value)} data-testid="contract-number" /></F>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="Verwaltungs-Typ">
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="contract-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Eigentümer">
            <Select value={form.owner_contact_id} onValueChange={(v) => set("owner_contact_id", v)}>
              <SelectTrigger data-testid="contract-owner"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="Vertragsbeginn"><Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="contract-start" /></F>
          <F label="Vertragsende (leer = unbefristet)"><Input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="contract-end" /></F>
        </div>

        {/* 2026-08 — Objekt-Zuordnung mit Gültig-Ab/Bis */}
        <div>
          <Label className="text-xs">Verknüpfte Objekte ({(form.property_assignments || []).length})</Label>
          <div className="text-[11px] text-slate-500 mb-1.5">Optional: für jedes Objekt einen Gültigkeitszeitraum hinterlegen. Die Vergütung greift automatisch erst ab &bdquo;Gültig ab&ldquo;.</div>
          <div className="border border-slate-200 rounded-md p-2 max-h-56 overflow-y-auto bg-slate-50/40 space-y-1" data-testid="contract-properties">
            {properties.length === 0 && <div className="text-xs text-slate-500 italic">Keine Objekte vorhanden</div>}
            {properties.map((p) => {
              const on = isAssigned(p.id);
              const a = getAssignment(p.id);
              return (
                <div key={p.id} className={`rounded px-2 py-1 ${on ? "bg-white border border-slate-200" : "hover:bg-white"}`}>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={on} onChange={() => toggleProperty(p.id)} data-testid={`contract-prop-${p.id}`} />
                    <span className="font-medium">{p.property_number ? `${p.property_number} · ` : ""}{p.name}</span>
                  </label>
                  {on && (
                    <div className="grid grid-cols-2 gap-2 mt-1.5 ml-6">
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Gültig ab</Label>
                        <Input type="date" className="h-7 text-xs" value={a?.valid_from || ""} onChange={(e) => updateAssignment(p.id, { valid_from: e.target.value })} data-testid={`contract-prop-from-${p.id}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Gültig bis (optional)</Label>
                        <Input type="date" className="h-7 text-xs" value={a?.valid_until || ""} onChange={(e) => updateAssignment(p.id, { valid_until: e.target.value })} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 2026-08 — Honorar-Grundwerte (aktuell) */}
        <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
          <div className="text-xs font-semibold text-slate-700 mb-2">Honorar (Grundeinstellung / aktueller Wert)</div>
          <div className="grid grid-cols-4 gap-3">
            <F label="Betrag (€)"><Input type="number" step="0.01" value={form.fee_amount} onChange={(e) => set("fee_amount", e.target.value)} data-testid="contract-fee-amount" /></F>
            <F label="Intervall">
              <Select value={form.fee_interval} onValueChange={(v) => set("fee_interval", v)}>
                <SelectTrigger data-testid="contract-fee-interval"><SelectValue /></SelectTrigger>
                <SelectContent>{FEE_INTERVALS.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Pro Einheit?">
              <Select value={String(form.fee_per_unit)} onValueChange={(v) => set("fee_per_unit", v === "true")}>
                <SelectTrigger data-testid="contract-fee-per-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">Nein – Pauschal</SelectItem>
                  <SelectItem value="true">Ja – pro Einheit</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Netto/Brutto">
              <Select value={String(form.is_net)} onValueChange={(v) => set("is_net", v === "true")}>
                <SelectTrigger data-testid="contract-fee-net"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">Brutto (inkl. USt.)</SelectItem>
                  <SelectItem value="true">Netto (zzgl. USt.)</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          {form.is_net && (
            <div className="mt-2 grid grid-cols-4 gap-3">
              <F label="USt.-Satz (%)"><Input type="number" step="0.1" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} data-testid="contract-vat-rate" /></F>
            </div>
          )}
        </div>

        {/* 2026-08 — Honorar-Historie (rückwirkende Änderungen) */}
        <div className="border border-slate-200 rounded-md p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs font-semibold text-slate-700">Honorar-Historie</div>
              <div className="text-[11px] text-slate-500">Zeitverlauf mit rückwirkenden Änderungen — der jüngste Eintrag mit &bdquo;gültig ab &le; heute&ldquo; ist aktiv.</div>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addFeeEntry} data-testid="contract-fee-history-add">
              + Eintrag
            </Button>
          </div>
          {(form.fee_history || []).length === 0 && (
            <div className="text-xs text-slate-500 italic p-2 bg-slate-50 rounded">Kein Verlauf — beim Speichern wird der oben eingestellte Wert als erster Historien-Eintrag erzeugt (ab Vertragsbeginn).</div>
          )}
          {(form.fee_history || []).map((h, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end mt-2 pt-2 border-t border-slate-100 first:border-t-0 first:mt-0 first:pt-0" data-testid={`fee-history-entry-${i}`}>
              <div className="col-span-2"><Label className="text-[10px] uppercase text-slate-500">Gültig ab</Label><Input type="date" className="h-8 text-xs" value={h.valid_from || ""} onChange={(e) => updateFeeEntry(i, { valid_from: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-[10px] uppercase text-slate-500">Gültig bis</Label><Input type="date" className="h-8 text-xs" value={h.valid_until || ""} onChange={(e) => updateFeeEntry(i, { valid_until: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-[10px] uppercase text-slate-500">Betrag</Label><Input type="number" step="0.01" className="h-8 text-xs tabular-nums" value={h.fee_amount} onChange={(e) => updateFeeEntry(i, { fee_amount: e.target.value })} /></div>
              <div className="col-span-1"><Label className="text-[10px] uppercase text-slate-500">Netto?</Label>
                <Select value={String(h.is_net)} onValueChange={(v) => updateFeeEntry(i, { is_net: v === "true" })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="false">Brutto</SelectItem><SelectItem value="true">Netto</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-1"><Label className="text-[10px] uppercase text-slate-500">USt %</Label><Input type="number" step="0.1" className="h-8 text-xs tabular-nums" value={h.vat_rate ?? 19} onChange={(e) => updateFeeEntry(i, { vat_rate: e.target.value })} /></div>
              <div className="col-span-3"><Label className="text-[10px] uppercase text-slate-500">Notiz</Label><Input className="h-8 text-xs" value={h.note || ""} onChange={(e) => updateFeeEntry(i, { note: e.target.value })} placeholder="z. B. Nachtrag Nr. 2" /></div>
              <div className="col-span-1 flex justify-end pb-0.5">
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-rose-700 hover:bg-rose-50" onClick={() => removeFeeEntry(i)} data-testid={`fee-history-remove-${i}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <F label="Umfang der Verwaltungstätigkeit">
          <Textarea rows={3} value={form.scope_description} onChange={(e) => set("scope_description", e.target.value)} placeholder="z. B. kaufmännische und technische Verwaltung gemäß § 27 WEG…" data-testid="contract-scope" />
        </F>

        <div>
          <Label className="text-xs">Verknüpfte Dokumente ({(form.linked_document_ids || []).length})</Label>
          <div className="border border-slate-200 rounded-md p-2 max-h-32 overflow-y-auto bg-slate-50/40">
            {documents.length === 0 && <div className="text-xs text-slate-500 italic">Keine Dokumente vorhanden</div>}
            {documents.slice(0, 50).map((d) => (
              <label key={d.id} className="flex items-center gap-2 text-xs py-0.5 hover:bg-white cursor-pointer rounded px-1">
                <input type="checkbox" checked={(form.linked_document_ids || []).includes(d.id)} onChange={() => toggleDocId(d.id)} />
                {d.title} <span className="text-slate-400 text-[10px]">({d.category})</span>
              </label>
            ))}
            {documents.length > 50 && <div className="text-[10px] text-slate-400 mt-1">… und {documents.length - 50} weitere (Suche im Dokumente-Modul)</div>}
          </div>
        </div>

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="contract-save">
            {editing ? "Aktualisieren" : "Anlegen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function F({ label, children }) {
  return <div><Label className="text-xs text-slate-700">{label}</Label><div className="mt-1">{children}</div></div>;
}
